/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { AuditAppPaging, AuditEntryEntry, AuditEntryPaging } from '../model';
import { BaseApi } from './base.api';
import { throwIfNotDefined } from '../../../assert';
import { buildCollectionParam } from '../../../alfrescoApiClient';
export class AuditApi extends BaseApi {
    deleteAuditEntriesForAuditApp(auditApplicationId, where) {
        throwIfNotDefined(auditApplicationId, 'auditApplicationId');
        throwIfNotDefined(where, 'where');
        const pathParams = {
            auditApplicationId
        };
        const queryParams = {
            where
        };
        return this.delete({
            path: '/audit-applications/{auditApplicationId}/audit-entries',
            pathParams,
            queryParams
        });
    }
    deleteAuditEntry(auditApplicationId, auditEntryId) {
        throwIfNotDefined(auditApplicationId, 'auditApplicationId');
        throwIfNotDefined(auditEntryId, 'auditEntryId');
        const pathParams = {
            auditApplicationId,
            auditEntryId
        };
        return this.delete({
            path: '/audit-applications/{auditApplicationId}/audit-entries/{auditEntryId}',
            pathParams
        });
    }
    getAuditApp(auditApplicationId, opts) {
        throwIfNotDefined(auditApplicationId, 'auditApplicationId');
        const pathParams = {
            auditApplicationId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv'),
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv')
        };
        return this.get({
            path: '/audit-applications/{auditApplicationId}',
            pathParams,
            queryParams
        });
    }
    getAuditEntry(auditApplicationId, auditEntryId, opts) {
        throwIfNotDefined(auditApplicationId, 'auditApplicationId');
        throwIfNotDefined(auditEntryId, 'auditEntryId');
        const pathParams = {
            auditApplicationId,
            auditEntryId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/audit-applications/{auditApplicationId}/audit-entries/{auditEntryId}',
            pathParams,
            queryParams,
            returnType: AuditEntryEntry
        });
    }
    listAuditApps(opts) {
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/audit-applications',
            queryParams,
            returnType: AuditAppPaging
        });
    }
    listAuditEntriesForAuditApp(auditApplicationId, opts) {
        throwIfNotDefined(auditApplicationId, 'auditApplicationId');
        opts = opts || {};
        const pathParams = {
            auditApplicationId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/audit-applications/{auditApplicationId}/audit-entries',
            pathParams,
            queryParams,
            returnType: AuditEntryPaging
        });
    }
    listAuditEntriesForNode(nodeId, opts) {
        throwIfNotDefined(nodeId, 'nodeId');
        opts = opts || {};
        const pathParams = {
            nodeId
        };
        const queryParams = {
            skipCount: opts === null || opts === void 0 ? void 0 : opts.skipCount,
            orderBy: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.orderBy, 'csv'),
            maxItems: opts === null || opts === void 0 ? void 0 : opts.maxItems,
            where: opts === null || opts === void 0 ? void 0 : opts.where,
            include: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.include, 'csv'),
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.get({
            path: '/nodes/{nodeId}/audit-entries',
            pathParams,
            queryParams,
            returnType: AuditEntryPaging
        });
    }
    updateAuditApp(auditApplicationId, auditAppBodyUpdate, opts) {
        throwIfNotDefined(auditApplicationId, 'auditApplicationId');
        throwIfNotDefined(auditAppBodyUpdate, 'auditAppBodyUpdate');
        const pathParams = {
            auditApplicationId
        };
        const queryParams = {
            fields: buildCollectionParam(opts === null || opts === void 0 ? void 0 : opts.fields, 'csv')
        };
        return this.put({
            path: '/audit-applications/{auditApplicationId}',
            pathParams,
            queryParams,
            bodyParam: auditAppBodyUpdate
        });
    }
}
